import { defineAsyncComponent, ref } from "vue";
import { useForm } from "vee-validate";
import IMask from "imask";
import NykAlert from "../../NykAlert/NykAlert.vue";
import Modal from "../../Modal/Modal.vue";
import formSettings from '../settings.js';
import { useRecaptchaToken } from '../../../composables/use-recaptcha.composable';

export default {
    components: {
        NykAlert,
        Modal,
    },
    props: {
        formId: {
            type: String
        },
        formTitle: {
            type: String
        },
        formGuid: {
            type: String
        },
        apiPath: {
            type: String
        },
        antiForgeryToken: {
            type: String
        },
        formElements: {
            type: Array,
            default: () => []
        },
        showSummarizedData: {
            type: Boolean,
        },
        summaryConfirmationButtonLabel: {
            type: String
        },
        summaryCancellationButtonLabel: {
            type: String
        },
        confirmationMessage: {
            type: String
        },
        submitSuccessMessage: {
            type: String,
            default: "Formularen er blevet sendt!"
        },
        submitErrorMessage: {
            type: String,
            default: "Der opstår en fejl med insendt!"
        },
        resetAfterSubmission: {
            type: Boolean
        },
        queryParams: {
            type: Object,
            default: () => ({})
        },
        queryParamFields: {
            type: Array,
            default: []
        }
    },
    setup(props) {
        const fieldPrefix = '';
        const submissionSucceeded = ref(false);
        const submitting = ref(false);
        const showStatusMessage = ref(false);
        const showConfirmationModal = ref(false);
        const confirmationData = ref([]);
        const { getRecaptchaToken } = useRecaptchaToken();

        // Cache for components to avoid re-importing which causes infinite reactivity updates on the forms 'values'
        const componentCache = {};

        const getComponentType = (element) => {
            const componentMap = {
                CustomSelectElementBlock: "NykSelect",
                CustomFileUploadElementBlock: "NykInputFile",
                CustomDateTimeElementBlock: "NykDateTimePicker",
                CustomParagraphElementBlock: 'NykFormParagraph',
                CustomTitleElementBlock: 'NykFormTitle',
                CustomTextboxElementBlock: "NykInput",
                CustomCprNumberElementBlock: "NykInput",
                CustomNumberElementBlock: "NykInput",
                CustomEmailElementBlock: "NykInput",
                CustomTextareaElementBlock: "NykTextarea",
                CustomAddressElementBlock: element.properties?.autoCompleteAddress ? "NykAddress" : "NykInput",
                CustomChoiceElementBlock: element.properties.allowMultiSelect ? "NykCheckboxGroup" : "NykRadioGroup",
                CustomPhoneElementBlock: "NykPhone",
                CustomCvrNumberElementBlock: "NykCvr",
                CustomSubmitButtonElementBlock: "NykSubmitButton",
                NykAdvisorSelector: "NykAdvisorSelector",
                NykCheckbox: "NykCheckbox",
            };

            const componentName = componentMap[element.contentType];

            if (!componentCache[componentName]) {
                componentCache[componentName] = defineAsyncComponent(() =>
                    import(`../${componentName}/${componentName}.vue`)
                );
            }

            return componentCache[componentName];
        };

        const getElementEvents = (element) => {
            if (element.contentType === "CustomCvrNumberElementBlock") {
                return {
                    update: onFieldUpdate
                }
            }
            return {};
        };

        const isRequired = (elementProperties) => {
            const validators = elementProperties.validators;

            if (validators) {
                return !!validators.find(validator => validator.type === "RequiredValidator");
            }

            return undefined;
        };

        const { handleSubmit, values, meta, resetForm, setFieldValue } = useForm();

        const buildQueryParams = (formValues) => {
            const formFieldsAsQueryParams = props.queryParamFields.reduce((acc, fieldName) => {
                if (formValues[fieldName]) {
                    acc[fieldName] = formValues[fieldName];
                }
                return acc;
            }, {});

            return {
                ...props.formId ? { formId: props.formId } : {},
                ...props.queryParams,
                ...formFieldsAsQueryParams
            };
        };

        const onSubmit = handleSubmit(async formValues => {
            if (props.showSummarizedData) {
                buildConfirmationData(formValues);
                showConfirmationModal.value = true;
                return;
            }

            await submitForm(formValues);
        });

        const buildConfirmationData = (formValues) => {
            confirmationData.value = Object.entries(formValues)
                .map(([key, value]) => {
                    const element = props.formElements.find(el => el.key === key);
                    const keyParts = key.split(formSettings.subFieldDelimiter);
                    const fallbackLabel = keyParts.length > 1 ? keyParts[1] : key;
                    const elementLabel = element ? element.properties.label : fallbackLabel;

                    let displayValue = value;
                    if (Array.isArray(value) && value.length && value[0] instanceof File) {
                        displayValue = value.map(f => f.name).join(', ') || formSettings.emptyValue;
                    } else if (Array.isArray(value)) {
                        displayValue = value.join(', ') || formSettings.emptyValue;
                    } else if (!value) {
                        displayValue = formSettings.emptyValue;
                    }

                    return {
                        label: elementLabel,
                        value: displayValue
                    };
                });
        };

        const confirmSubmission = async () => {
            showConfirmationModal.value = false;
            await submitForm(Object.fromEntries(
                Object.entries(values).filter(([key, value]) => value !== undefined && value !== '')
            ));
        };

        const cancelSubmission = () => {
            showConfirmationModal.value = false;
        };

        const submitForm = async (formValues) => {
            submitting.value = true;
            showStatusMessage.value = false;
            submissionSucceeded.value = false;

            const formMetaData = {
                ...props.formId ? { formId: props.formId } : {},
                ...props.formTitle ? { formTitle: props.formTitle } : {},
                ...props.formGuid ? { formGuid: props.formGuid } : {}
            }
            try {
                const queryParamsObj = buildQueryParams(formValues);
                const queryString = new URLSearchParams(queryParamsObj).toString();
                const finalUrl = props.apiPath + "?" + queryString;

                const response = await fetch(finalUrl, {
                    method: 'POST',
                    body: getFormData(formValues, formMetaData),
                    headers: {
                        RequestVerificationToken:
                            props.antiForgeryToken,
                        RecaptchaToken: await getRecaptchaToken()
                    }
                });

                const data = await response.json();

                if (response.status === 200) {
                    submissionSucceeded.value = true;
                } else {
                    submissionSucceeded.value = false;
                }
            }
            catch (error) {
                submissionSucceeded.value = false;
                console.error("Error submitting form:", error);
            }
            finally {
                if (props.resetAfterSubmission) {
                    resetForm();
                }

                submitting.value = false;
                showStatusMessage.value = true;
            }
        };

        const getFormData = (formValues, formMetaData) => {
            const formData = new FormData();
            let hasFiles = false;

            const formularData = Object.entries(formValues).reduce((data, [name, value]) => {
                const normalizedName = name.replace(fieldPrefix, "");
                const element = props.formElements.find(el => el.key === name);
                let valueType = Array.isArray(value) ? 'array' : typeof value;

                if (element && element.contentType === 'CustomDateTimeElementBlock') {
                    valueType = 'date';
                }

                if (valueType === 'array' && value.length !== 0 && value[0] instanceof File) {
                    value.forEach(file => formData.append('file', file, file.name));
                    value = value.map(file => file.name);
                    hasFiles = true;
                }

                return [
                    ...data,
                    {
                        name,
                        name: element ? element.displayName : normalizedName,
                        label: element ? element.properties.label : '',
                        value: ['object', 'array', 'boolean', 'number'].includes(valueType)
                            ? JSON.stringify(value)
                            : value,
                        type: valueType
                    }]
            }, []);

            const fullData = {
                ...formMetaData,
                hasFiles,
                formularData: formularData
            };

            formData.append('formData', JSON.stringify(fullData));
            return formData;
        };

        const checkVisibilityCondition = (condition) => {
            const comparingFieldName = `${fieldPrefix}${condition.field}`;
            const valueToCompareAgainst = condition.fieldValue;

            if (condition.operator === "Equals") {
                return values[comparingFieldName] === valueToCompareAgainst;
            }

            if (condition.operator === "NotEquals") {
                return values[comparingFieldName] !== valueToCompareAgainst;
            }

            if (condition.operator === "Contains") {
                return values[comparingFieldName]?.includes(valueToCompareAgainst);
            }

            if (condition.operator === "NotContains") {
                return !values[comparingFieldName]?.includes(valueToCompareAgainst);
            }

            if (condition.operator === "MatchRegularExpression") {
                const regex = new RegExp(valueToCompareAgainst);
                return regex.test(values[comparingFieldName]);
            }

            return true;
        };

        const isVisible = (element) => {
            if (!element.properties.conditions) return true;

            const conditionsSatisfied = element.properties.conditionCombination === "Any"
                ? element.properties.conditions.some(checkVisibilityCondition)
                : element.properties.conditions.every(checkVisibilityCondition);

            if (element.properties.satisfiedAction && element.properties.satisfiedAction === "hide") {
                return !conditionsSatisfied;
            }

            return conditionsSatisfied;
        };

        const extraElementSpecificProps = (element, rest) => {
            if (element.contentType === "CustomNumberElementBlock") {
                return {
                    format: {
                        mask: Number,
                        ...element.properties.noThousandSeperator ? {} : { thousandsSeparator: ',' },
                        ...element.properties.allowDecimal ? {
                            scale: 2, radix: '.',
                            mapToRadix: ['.'],
                        } : {
                            scale: 0
                        },
                    },
                    inputMode: 'numeric',
                    ...rest
                };
            }
            else if (element.contentType === "CustomCvrNumberElementBlock") {
                const elementProperties = Object.entries(rest);

                const relatedFields = elementProperties
                    .filter(([field]) => !formSettings.primitiveProps.includes(field))
                    .reduce((fields, [field, value]) => ({ ...fields, [field]: value }), {});

                rest = elementProperties
                    .filter(([field]) => formSettings.primitiveProps.includes(field))
                    .reduce((fields, [field, value]) => ({ ...fields, [`${field}`]: value }), {});

                return {
                    relatedFields,
                    ...rest
                };
            }
            else if (element.contentType === "CustomCprNumberElementBlock") {
                return {
                    format: {
                        mask: 'DDMMYY 0000',
                        blocks: {
                            DD: {
                                mask: IMask.MaskedRange,
                                from: 1,
                                to: 31,
                            },
                            MM: {
                                mask: IMask.MaskedRange,
                                from: 1,
                                to: 12,
                            },
                            YY: {
                                mask: IMask.MaskedRange,
                                from: 0,
                                to: 99,
                            }
                        }
                    },
                    inputMode: 'numeric',
                    ...rest
                };
            }

            else if (element.contentType === "CustomEmailElementBlock") {
                return {
                    ...rest,
                    type: 'email',
                    validators: [
                        ...element.properties.validators,
                        {
                            type: 'EmailFormatValidator',
                            model: {
                                message: element.properties.invalidEmailFormatErrorMessage
                            }
                        },
                        ...element.properties.validDomains &&
                            element.properties.validDomains.length > 0
                            ? [{
                                type: 'EmailDomainValidator',
                                model: {
                                    message: element.properties.invalidDomainErrorMessage,
                                    validDomains: element.properties.validDomains || []
                                }
                            }]
                            : []
                    ]
                };
            }

            else if (element.contentType === "CustomSubmitButtonElementBlock") {
                return {
                    isParentFormValid: meta.value.valid,
                    submitting: submitting.value,
                    ...rest
                };
            }

            return { ...rest };
        };

        const getStaticElementProps = () => {
            return props.formElements.reduce((map, element) => {
                const { type, ...rest } = element.properties;

                return {
                    ...map,
                    [element.key]: {
                        name: `${fieldPrefix + element.key}`,
                        required: isRequired(element.properties),
                        ...extraElementSpecificProps(element, rest)
                    }
                };
            }, {})
        };

        const onFieldUpdate = (updates) => {
            Object.entries(updates).forEach(([fieldName, value]) => {
                const element = props.formElements.find(el => el.displayName === fieldName);
                if (element && value) {
                    setFieldValue(element.key, value)
                }
            })
        };

        return {
            submissionSucceeded,
            showStatusMessage,
            showConfirmationModal,
            confirmationData,
            values,
            staticElementProps: ref(getStaticElementProps()),
            isVisible,
            onSubmit,
            confirmSubmission,
            cancelSubmission,
            getComponentType,
            getElementEvents,
            onFieldUpdate
        };
    },
}